import { useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Loader2, Play, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIvrEditor } from '../../context';
import type { IvrNodeData, PlayAction } from '../../types';
import { NodeCard } from '../NodeCard';

export function PlayNode(props: NodeProps) {
  const { updateNodeData, onRemove, prompts } = useIvrEditor();
  const data = props.data as IvrNodeData;
  const action = (data.action ?? {}) as PlayAction;

  const [open, setOpen] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  const welcomePrompts = prompts.filter(
    (p) =>
      p.promptCategory === 'welcome' &&
      (p.promptStatus === undefined ||
        Number(p.promptStatus) === 1 ||
        p.promptStatus === '1'),
  );

  const onPick = (promptIdStr: string) => {
    const picked = welcomePrompts.find(
      (p) => String(p.promptId) === promptIdStr,
    );
    if (!picked) return;
    updateNodeData(props.id, {
      action: {
        type: 'play',
        id: picked.promptId,
        audio: (picked as { promptUrl?: string }).promptUrl ?? '',
        duration: (picked as { promptDuration?: number }).promptDuration ?? 0,
        name: picked.fileName,
      } as PlayAction,
    });
  };

  const togglePreview = () => {
    if (!action.audio) return;
    if (audio && playingId === action.audio) {
      audio.pause();
      setPlayingId(null);
      setAudio(null);
      return;
    }
    const a = new Audio(action.audio);
    a.onended = () => {
      setPlayingId(null);
      setAudio(null);
    };
    a.play();
    setAudio(a);
    setPlayingId(action.audio);
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-white !bg-sky-500"
      />
      <NodeCard
        verb="play"
        icon={Volume2}
        label="Play"
        status={action.audio ? 'configured' : 'incomplete'}
        selected={props.selected}
        summary={
          action.name ? (
            <span className="truncate font-mono">{action.name}</span>
          ) : (
            <span className="text-muted-foreground">Pick an audio file</span>
          )
        }
        onConfigure={() => setOpen(true)}
        onRemove={() => onRemove(props.id)}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3 !w-3 !border-2 !border-white !bg-sky-500"
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Play prompt</SheetTitle>
            <SheetDescription>
              Pick a welcome prompt to play to the caller at this step.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label>
                Audio
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select value={String(action.id ?? '')} onValueChange={onPick}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      welcomePrompts.length === 0
                        ? 'No welcome prompts uploaded'
                        : 'Pick a welcome prompt'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {welcomePrompts.map((p) => (
                    <SelectItem key={p.promptId} value={String(p.promptId)}>
                      {p.fileName ?? p.promptName ?? `#${p.promptId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {action.audio && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={togglePreview}
              >
                {playingId === action.audio ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {playingId === action.audio ? 'Playing…' : 'Preview'}
              </Button>
            )}
          </div>
          <SheetFooter>
            <Button onClick={() => setOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
